<script lang="ts">
	import { Badge, Heading } from "flowbite-svelte";
	import { getContext, type Snippet } from "svelte";
	import { twMerge, type ClassNameValue } from "tailwind-merge";
	import type { DetailItem, LandingPage } from "#gen/bundles/v1/bundle_pb.js";
	import type { Mode } from "#gen/bundles/v1/service_pb.js";
	import Icon from "#lib/components/Icon.svelte";
	import TokenizedText from "#lib/components/TokenizedText.svelte";
	import ImageCarousel from "#lib/components/flow/viewer/ImageCarousel.svelte";
	import { getTextColor } from "#lib/theme.js";
	import type { TokenProvider } from "#lib/tokenProvider.js";
	import type { Writable } from "svelte/store";

	interface Props {
		landingPage: LandingPage;
		mode: Mode;
		hideGradient?: boolean;
		class?: ClassNameValue;
		conversion?: Snippet;
	}

	const tokenProvider: Writable<TokenProvider> = getContext("tokenProvider");

	const { landingPage, hideGradient = false, class: className, conversion, mode }: Props = $props();

	function shouldCenter(highlightText: DetailItem | undefined, tertiaryText: DetailItem[]) {
		if (highlightText && tertiaryText.length > 1) {
			return false;
		}
		if (!highlightText && tertiaryText.length > 2) {
			return false;
		}
		return true;
	}
</script>

<ImageCarousel images={landingPage.images} {hideGradient} centerDots={false} class={twMerge(className, "relative mt-1 sm:max-h-170")}>
	{#snippet overlay()}
		<div class="grow"></div>
		<div class="flex-none">
			<Heading tag="h1" style="color: var(--header-color);" class="pb-1 text-center text-lg leading-tight font-semibold">
				<TokenizedText provider={$tokenProvider} text={landingPage.primaryText} {mode} />
			</Heading>
		</div>
		{#if landingPage.secondaryText && landingPage.secondaryText.text}
			<div class="flex-none text-gray-300">
				<p class="ellipsis mb-1 text-center text-sm" style="color: var(--paragraph-color, #e5e7eb);">
					{#if landingPage.secondaryText.icon}
						<Icon class="inline-block" iconSet={landingPage.secondaryText.iconSet} name={landingPage.secondaryText.icon} />
					{/if}
					<TokenizedText provider={$tokenProvider} text={landingPage.secondaryText.text} {mode} />
				</p>
			</div>
		{/if}
		<div class="no-scrollbar w-full flex-none overflow-x-auto pt-2 pb-4 text-center whitespace-nowrap">
			<div class="flex flex-nowrap items-center gap-1" class:justify-center={shouldCenter(landingPage.highlightText, landingPage.tertiaryText)}>
				{#if landingPage.highlightText}
					{@const color = landingPage.highlightText?.colorHex || "#374151"}
					<Badge large rounded class="flex flex-none items-center gap-1 px-3 py-1 text-sm" style="background-color: {color}; color: {getTextColor(color)};">
						<TokenizedText provider={$tokenProvider} text={landingPage.highlightText.text} {mode} />
					</Badge>
				{/if}
				{#each landingPage.tertiaryText as item (item)}
					{#if item.text || item.icon}
						{@const color = item.colorHex || "#374151"}
						<Badge rounded class="flex flex-none items-center gap-1 px-3 py-1 text-sm" style="background-color: {color}; color: {getTextColor(color)};">
							{#if item.icon}
								<Icon iconSet={item.iconSet} name={item.icon} />
							{/if}
							<TokenizedText provider={$tokenProvider} text={item.text} {mode} />
						</Badge>
					{/if}
				{/each}
			</div>
		</div>
		{@render conversion?.()}
	{/snippet}
</ImageCarousel>
